// İSG uzmanı portalı — tehlike kalemi oluştur/düzenle formu (Modal + RHF + Zod). api-contract
// §19.3: `POST /risk-assessments/{uuid}/items` (create) / `PUT .../items/{itemUuid}` (update).
// Alanlar: tehlike (zorunlu) + risk + etkilenenler + mevcut önlemler + **Olasılık×Şiddet 5x5
// matris** + alınacak önlem + **artık risk** matrisi.
//
// **⛔ score/level GÖNDERİLMEZ (§19).** Kalem yalnız `probability`/`severity` (+ residual) taşır;
// matris rozeti canlı GÖSTERİM içindir, kalıcı band sunucudandır. Yalnız düzenlenebilir
// değerlendirmede açılır (BE `409 RISK_ASSESSMENT_NOT_EDITABLE` UI'da önlenir).

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Field,
  Input,
  Modal,
  type RiskItemDto,
  Spinner,
  resolveApiErrorMessage,
  riskApi,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { riskKeys } from '../../lib/riskKeys';
import { RiskItemScoreFields } from './RiskItemScoreFields';
import { type RiskItemFormValues, toRiskItemRequest } from './riskEditorForm';

interface RiskItemFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assessmentUuid: string;
  /** Verilirse düzenleme; yoksa oluşturma. */
  item?: RiskItemDto | null;
}

export function RiskItemFormModal({
  open,
  onOpenChange,
  assessmentUuid,
  item,
}: RiskItemFormModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = Boolean(item);

  const schema = useMemo(
    () =>
      z
        .object({
          hazard: z.string().trim().min(1, t('risk.editor.validation.hazardRequired')),
          risk: z.string(),
          affectedPersons: z.string(),
          existingControls: z.string(),
          probability: z.number().nullable(),
          severity: z.number().nullable(),
          recommendedControls: z.string(),
          residualProbability: z.number().nullable(),
          residualSeverity: z.number().nullable(),
        })
        .superRefine((values, ctx) => {
          if (values.probability == null || values.severity == null) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['probability'],
              message: t('risk.editor.validation.scoreRequired'),
            });
          }
          if ((values.residualProbability == null) !== (values.residualSeverity == null)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['residualProbability'],
              message: t('risk.editor.validation.residualIncomplete'),
            });
          }
        }),
    [t],
  );

  const { register, handleSubmit, reset, watch, setValue, trigger, formState } =
    useForm<RiskItemFormValues>({
      resolver: zodResolver(schema),
      values: {
        hazard: item?.hazard ?? '',
        risk: item?.risk ?? '',
        affectedPersons: item?.affectedPersons ?? '',
        existingControls: item?.existingControls ?? '',
        probability: item?.probability ?? null,
        severity: item?.severity ?? null,
        recommendedControls: item?.recommendedControls ?? '',
        residualProbability: item?.residualProbability ?? null,
        residualSeverity: item?.residualSeverity ?? null,
      },
    });

  const mutation = useMutation({
    mutationFn: (values: RiskItemFormValues) => {
      const body = toRiskItemRequest(values);
      return item
        ? riskApi.updateRiskItem(assessmentUuid, item.uuid, body)
        : riskApi.createRiskItem(assessmentUuid, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: riskKeys.detail(assessmentUuid) });
      queryClient.invalidateQueries({ queryKey: riskKeys.list() });
      reset();
      onOpenChange(false);
    },
  });

  const probability = watch('probability');
  const severity = watch('severity');
  const residualProbability = watch('residualProbability');
  const residualSeverity = watch('residualSeverity');

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          mutation.reset();
          reset();
        }
        onOpenChange(next);
      }}
      title={isEdit ? t('risk.item.edit.title') : t('risk.item.add.title')}
      description={t('risk.item.form.description')}
      size="lg"
      closeLabel={t('risk.common.close')}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('risk.common.cancel')}
          </Button>
          <Button type="submit" form="risk-item-form" loading={mutation.isPending}>
            {mutation.isPending ? (
              <Spinner label={t('risk.common.save')} />
            ) : isEdit ? (
              t('risk.common.save')
            ) : (
              t('risk.common.add')
            )}
          </Button>
        </>
      }
    >
      <form
        id="risk-item-form"
        className="flex flex-col gap-4"
        onSubmit={handleSubmit((values) => mutation.mutate(values))}
        noValidate
      >
        {mutation.isError ? (
          <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
        ) : null}

        <Field
          htmlFor="item-hazard"
          label={t('risk.item.field.hazard')}
          error={formState.errors.hazard?.message}
          required
        >
          <Input
            id="item-hazard"
            invalid={Boolean(formState.errors.hazard)}
            {...register('hazard')}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field htmlFor="item-risk" label={t('risk.item.field.risk')}>
            <Input id="item-risk" {...register('risk')} />
          </Field>
          <Field htmlFor="item-affected" label={t('risk.item.field.affectedPersons')}>
            <Input id="item-affected" {...register('affectedPersons')} />
          </Field>
          <Field htmlFor="item-existing" label={t('risk.item.field.existingControls')}>
            <Input id="item-existing" {...register('existingControls')} />
          </Field>
          <Field htmlFor="item-recommended" label={t('risk.item.field.recommendedControls')}>
            <Input id="item-recommended" {...register('recommendedControls')} />
          </Field>
        </div>

        <RiskItemScoreFields
          idPrefix="item-modal"
          probability={probability}
          severity={severity}
          onScore={(p, s) => {
            // owner review B4-2: `superRefine` koyar hatayı TEK path'e (`probability`) — iki ayrı
            // `setValue({shouldValidate:true})` çağrısı, İKİNCİ validasyonu yalnız `severity` path'i
            // için tetikler, `probability`'deki hata TEMİZLENMEZ (bayat "Lütfen matristen Olasılık
            // ve Şiddet seçin." mesajı, hücre seçiliyken bile kalır). Fix: ikisini de
            // shouldValidate:false ile yaz, SONRA ikisini birden tek `trigger` ile doğrula.
            setValue('probability', p, { shouldValidate: false });
            setValue('severity', s, { shouldValidate: false });
            void trigger(['probability', 'severity']);
          }}
          residualProbability={residualProbability}
          residualSeverity={residualSeverity}
          onResidual={(p, s) => {
            setValue('residualProbability', p, { shouldValidate: false });
            setValue('residualSeverity', s, { shouldValidate: false });
            void trigger(['residualProbability', 'residualSeverity']);
          }}
          onResidualClear={() => {
            setValue('residualProbability', null, { shouldValidate: false });
            setValue('residualSeverity', null, { shouldValidate: false });
            void trigger(['residualProbability', 'residualSeverity']);
          }}
          scoreError={formState.errors.probability?.message}
          residualError={formState.errors.residualProbability?.message}
        />
      </form>
    </Modal>
  );
}
